import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faFile, faTrash, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './OptimizeCampaignModal.css';

/**
 * GeofenceAdGroupsModal — collects an events Excel (+ radius, dry-run) and
 * hands the uploaded GCS path back to the parent, which POSTs to
 * /api/v2/campaigns/:id/create-geofence-adgroups. Mirrors OptimizeCampaignModal's
 * upload flow (reuses its CSS for consistent styling).
 *
 * When the campaign record maps to several TTD platform campaigns (e.g. a
 * multi-campaign import/orchestration), `campaignOptions` lets the user pick
 * which one to target via a single-select dropdown (id + name). Defaults to
 * `platformCampaignId`.
 */
const GeofenceAdGroupsModal = ({ isOpen, onClose, campaign, platformCampaignId, campaignOptions = [], accountId, user, onComplete }) => {
  const [files, setFiles] = useState([]);
  const [radius, setRadius] = useState(500);
  const [dryRun, setDryRun] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const fileInputRef = useRef(null);

  // Default the selection to the campaign the button was clicked for (or the
  // first available option) each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setSelectedCampaignId(platformCampaignId || campaignOptions?.[0]?.id || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, platformCampaignId]);

  if (!isOpen) return null;

  const isExcel = (name) => /\.(xlsx|xls)$/i.test(name);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFiles = (newFiles) => {
    const valid = newFiles.filter((f) => isExcel(f.name));
    if (valid.length !== newFiles.length) {
      alert('Only Excel files (.xlsx, .xls) are accepted');
    }
    // Single Excel — keep the most recent selection.
    if (valid.length > 0) setFiles([valid[valid.length - 1]]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e) => handleFiles(Array.from(e.target.files));

  const removeFile = () => setFiles([]);

  const handleSubmit = async () => {
    if (files.length === 0) {
      alert('Please upload an events Excel file (.xlsx)');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('accountId', accountId);
      formData.append('userId', user?.userId || user?.id);

      const uploadResponse = await api.post(
        getApiUrl('/api/upload'),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      const excelGcsPath = uploadResponse.data.data.gcsPath;
      console.log('📍 Geofence events Excel uploaded:', excelGcsPath);

      onComplete({
        platformCampaignId: selectedCampaignId || platformCampaignId,
        excelGcsPath,
        radius: Number.parseInt(radius, 10) > 0 ? Number.parseInt(radius, 10) : 500,
        dryRun,
      });
      onClose();
    } catch (error) {
      console.error('Geofence ad group creation failed to start:', error);
      alert('Failed to start: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content optimize-campaign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faLocationDot} /> Create Geofence Ad Groups
          </h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <div className="campaign-info">
            <p><strong>Campaign:</strong> {campaign?.name}</p>
            <p><strong>Platform:</strong> {campaign?.destination?.toUpperCase()}</p>
            {campaignOptions && campaignOptions.length > 0 ? (
              <div style={{ marginTop: '8px' }}>
                <label htmlFor="geofenceTtdCampaign" style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                  TTD Campaign
                </label>
                <select
                  id="geofenceTtdCampaign"
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                  disabled={isUploading}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' }}
                >
                  {campaignOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name ? `${c.name} (${c.id})` : c.id}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              platformCampaignId && <p><strong>TTD Campaign ID:</strong> {platformCampaignId}</p>
            )}
          </div>

          <div className="info-message">
            <p>
              Upload an events Excel (locations/dates). For each event we create a geofence, find nearby
              hotels, match Foursquare audiences, and build 4 ad groups. This runs in the background
              (~10–40 min) while geofences are provisioned in TTD.
            </p>
          </div>

          <div
            className={`file-drop-zone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faUpload} size="2x" />
            <p>Drag and drop the events Excel here or click to select</p>
            <small>Excel files (.xlsx, .xls)</small>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {files.length > 0 && (
            <div className="selected-files">
              <h4>Selected File:</h4>
              <div className="file-item">
                <FontAwesomeIcon icon={faFile} />
                <span className="file-name">{files[0].name}</span>
                <span className="file-size">({(files[0].size / 1024 / 1024).toFixed(2)} MB)</span>
                <button className="remove-file-btn" onClick={removeFile} disabled={isUploading}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginTop: '16px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <span><strong>Geofence radius (m):</strong></span>
              <input
                type="number"
                min="50"
                step="50"
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                disabled={isUploading}
                style={{ width: '90px', padding: '6px 8px', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                disabled={isUploading}
              />
              <span><strong>Dry run</strong> (build configs only, no TTD changes)</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={isUploading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isUploading || files.length === 0 || !(selectedCampaignId || platformCampaignId)}
            title={files.length === 0 ? 'Please upload an events Excel file' : (!(selectedCampaignId || platformCampaignId) ? 'Please select a TTD campaign' : '')}
          >
            {isUploading ? 'Starting…' : 'Create Geofence Ad Groups'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeofenceAdGroupsModal;
